// Les onglets de la tour : Demandes, À trier, Agents (et dispatchers),
// Projets.
import type { ComponentChildren, JSX } from 'preact'
import { useEffect, useRef, useState } from 'preact/hooks'
import type {
  TowerAgents, TowerOverview, TowerProject, TowerProposalPart, TowerRunner, TowerSeverity, TowerTicketDetail, TowerTicketRow, TowerTicketType, TowerTriageCard,
} from '@feedbaacks/sdk'
import { Button, cx, errorMessage, IconButton, Icons, Spinner, useFeedbaacks } from '@feedbaacks/ui'
import { href, onLinkClick } from '../router'
import {
  FILTERS, SEVERITY_META, STATE_META, TYPE_LABEL, archivable, confidenceTone, durationLabel, filterCounts, filterTickets, groupTickets, money,
  neighbour, nextAfterRemoval, relative, searchTickets, sourceLabel, triageActions, triageChip, type TicketFilter,
} from './tower-model'
import { useHotkeys, useTowerAct } from './tower-hooks'
import { Chip, TowerProgress, UntrustedOriginal } from './TowerParts'
import { TicketDetail } from './TowerTicket'
import { proposalAddable } from './TowerDemand'

function Loading(): JSX.Element {
  return <div class="dash-center"><Spinner size={24} /></div>
}

function Empty({ title, text, children }: { title: string; text: string; children?: ComponentChildren }): JSX.Element {
  return (
    <div class="tw-empty">
      <p class="tw-strong">{title}</p>
      <p class="fb-muted">{text}</p>
      {children}
    </div>
  )
}

function Stat({ label, value, sub, ratio, onClick, tone, testId }: {
  label: string; value: string | number; sub?: string; ratio?: number; onClick?: () => void; tone?: 'amber'; testId?: string
}): JSX.Element {
  const body = (
    <>
      <span class="fb-muted">{label}</span>
      <span class="tw-stat-value">
        <strong class={cx(tone === 'amber' && 'tw-amber')}>{value}</strong>
        {sub && <span class="fb-muted">{sub}</span>}
      </span>
      {ratio !== undefined && <span class="tw-bar"><span class={ratio > 0.8 ? 'tw-tone-bg-amber' : ''} style={{ width: `${Math.round(ratio * 100)}%` }} /></span>}
    </>
  )
  return onClick
    ? <button type="button" class="tw-stat is-link" onClick={onClick} data-testid={testId}>{body}</button>
    : <div class="tw-stat" data-testid={testId}>{body}</div>
}

// ── Demandes ────────────────────────────────────────────────────────────

export interface RequestsQuery { filter: TicketFilter; project: string; q: string }

function TicketLine({ t, selected, busy, onSelect, onArchive }: {
  t: TowerTicketRow; selected: boolean; busy: boolean; onSelect: () => void; onArchive: (archived: boolean) => void
}): JSX.Element {
  return (
    <li class={cx('tw-line', selected && 'is-selected')}>
      <button type="button" class="tw-ticket" onClick={onSelect} aria-current={selected ? 'true' : undefined} data-testid={`tower-ticket-${t.code}`}>
        <span class="tw-row">
          <code class="fb-mono fb-muted">{t.code}</code>
          {t.removed ? <Chip>Retiré</Chip> : <Chip tone={STATE_META[t.state].tone}>{STATE_META[t.state].label}</Chip>}
          {t.severity === 'high' || t.severity === 'critical' ? <Chip tone={SEVERITY_META[t.severity].tone}>{SEVERITY_META[t.severity].label}</Chip> : null}
          <span class="fb-muted tw-small tw-push" title={`Mis à jour le ${new Date(t.updated_at).toLocaleString('fr-FR')}`}>
            {relative(t.archived_at ?? t.updated_at)}
          </span>
        </span>
        <span class="tw-strong tw-clamp">{t.title}</span>
        {t.state === 'failed' && t.error && <span class="tw-small tw-ko tw-clamp-1">{t.error}</span>}
        {t.waiting && <span class="tw-small tw-wait tw-clamp-1" data-testid={`tower-waiting-${t.code}`}>{t.waiting}</span>}
        <span class="tw-row fb-muted tw-small">
          <span class="fb-mono">{t.project}</span>
          {t.depends_on.length > 0 && <span title="Tickets amont de la même demande">après {t.depends_on.join(', ')}</span>}
          <span>{sourceLabel(t.source, t.company)}</span>
          <span class="tw-push"><TowerProgress state={t.state} stage={t.stage} /></span>
        </span>
      </button>
      {t.can_archive && (
        <span class="tw-line-action">
          {t.archived_at
            ? <IconButton label={`Sortir ${t.code} des archives`} disabled={busy} onClick={() => onArchive(false)} testId={`tower-unarchive-${t.code}`}><Icons.ArchiveRestore size={15} /></IconButton>
            : <IconButton label={`Archiver ${t.code} (E)`} disabled={busy} onClick={() => onArchive(true)} testId={`tower-archive-${t.code}`}><Icons.Archive size={15} /></IconButton>}
        </span>
      )}
    </li>
  )
}

export function RequestsView({ overview, archived, query, onQuery, selected, onSelect, onTriage, isApprover, version, onChanged }: {
  overview: TowerOverview | null
  /** Les archives, chargées au premier passage sur le filtre. */
  archived: TowerTicketRow[] | null
  query: RequestsQuery
  onQuery: (patch: Partial<RequestsQuery>, replace?: boolean) => void
  selected: string | null
  onSelect: (code: string | null) => void
  onTriage: () => void
  isApprover: boolean
  version: number
  onChanged: () => void
}): JSX.Element {
  const { client, notify } = useFeedbaacks()
  const { busy, archive, archiveMany } = useTowerAct(onChanged)
  const [detail, setDetail] = useState<TowerTicketDetail | null>(null)
  const search = useRef<HTMLInputElement>(null)
  useEffect(() => {
    if (!selected) return
    let alive = true
    client.tower.ticket(selected).then((d) => alive && setDetail(d), (err) => notify('error', errorMessage(err, 'Ticket introuvable')))
    return () => {
      alive = false
    }
  }, [selected, version])

  const inArchive = query.filter === 'archives'
  const source = inArchive ? archived : overview?.tickets ?? null
  const scoped = source ? searchTickets(source, query.q, query.project) : []
  const rows = inArchive ? scoped : filterTickets(scoped, query.filter)
  const groups = groupTickets(rows, query.filter)
  // L'ordre affiché, groupes compris : celui des flèches du clavier.
  const ordered = groups.flatMap((g) => g.rows)
  const counts = filterCounts(overview ? searchTickets(overview.tickets, query.q, query.project) : [])

  const archiveOne = (t: TowerTicketRow, on: boolean) => {
    const next = t.code === selected ? nextAfterRemoval(ordered, t.code) : selected
    void archive(t, on).then((ok) => {
      if (ok && t.code === selected) onSelect(next)
    })
  }

  useHotkeys((e) => {
    if (e.key === 'ArrowDown' || e.key === 'j' || e.key === 'ArrowUp' || e.key === 'k') {
      const code = neighbour(ordered, selected, e.key === 'ArrowDown' || e.key === 'j' ? 1 : -1)
      if (!code) return
      e.preventDefault()
      onSelect(code)
      document.querySelector(`[data-testid="tower-ticket-${code}"]`)?.scrollIntoView({ block: 'nearest' })
    } else if (e.key === 'Escape' && selected) {
      onSelect(null)
    } else if (e.key === '/') {
      e.preventDefault()
      search.current?.focus()
    } else if (e.key === 'e' && selected) {
      const t = ordered.find((x) => x.code === selected)
      if (t?.can_archive) archiveOne(t, !t.archived_at)
    }
  })

  // Le détail affiché est celui du ticket sélectionné, jamais un précédent.
  const shown = detail && selected && (detail.code === selected || detail.id === selected) ? detail : null
  if (!overview) return <Loading />
  const projects = [...new Set([...overview.tickets, ...(archived ?? [])].map((t) => t.project))].sort()
  const bulk = archivable(query.filter === 'tous' ? groups.find((g) => g.key === 'termines')?.rows ?? [] : rows)
  const canBulk = !inArchive && bulk.length > 1 && (query.filter === 'tous' || query.filter === 'livres' || query.filter === 'echoues')

  return (
    <div class="tw-requests">
      <div class="tw-stats" data-testid="tower-stats">
        <Stat label="À trier" value={overview.to_triage} tone={overview.to_triage ? 'amber' : undefined} onClick={onTriage} testId="tower-stat-triage" />
        <Stat label="Attente prod" value={overview.awaiting_approval} tone={overview.awaiting_approval ? 'amber' : undefined}
          onClick={() => onQuery({ filter: 'attente' })} testId="tower-stat-awaiting" />
        <Stat label="Agents actifs" value={`${overview.active_agents} / ${overview.slots}`} />
        <Stat label="Livrés (7 j)" value={overview.delivered_week} sub={overview.delivered_week ? `médiane ${durationLabel(overview.median_seconds)}` : undefined} />
        <Stat label="Budget du jour" value={money(overview.spent_today_usd)} sub={`sur ${money(overview.daily_cap_usd)}`}
          ratio={overview.daily_cap_usd > 0 ? Math.min(1, overview.spent_today_usd / overview.daily_cap_usd) : 0} />
      </div>
      <div class={cx('tw-master', selected && 'has-detail')}>
        <div class="tw-list">
          <div class="tw-list-head">
            <div class="tw-search">
              <Icons.Search size={14} />
              <input ref={search} class="fb-input" type="search" placeholder="Rechercher un ticket  /" aria-label="Rechercher un ticket" value={query.q}
                data-testid="tower-search" onInput={(e) => onQuery({ q: e.currentTarget.value }, true)} />
              {projects.length > 1 && (
                <select class="fb-select" aria-label="Projet" value={query.project} data-testid="tower-project-filter"
                  onChange={(e) => onQuery({ project: e.currentTarget.value })}>
                  <option value="">Tous les projets</option>
                  {projects.map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
              )}
            </div>
            <div class="fb-chips tw-filters">
              {FILTERS.map((f) => (
                <button key={f.key} type="button" class={cx('fb-chip', query.filter === f.key && 'is-active')} onClick={() => onQuery({ filter: f.key })} data-testid={`tower-filter-${f.key}`}>
                  {f.label}<span class={cx('fb-chip-count', !counts[f.key] && 'is-zero')}>{counts[f.key]}</span>
                </button>
              ))}
              <button type="button" class={cx('fb-chip', inArchive && 'is-active')} onClick={() => onQuery({ filter: 'archives' })} data-testid="tower-filter-archives">
                <Icons.Archive size={12} />Archivés<span class={cx('fb-chip-count', !overview.archived && 'is-zero')}>{overview.archived}</span>
              </button>
            </div>
          </div>
          {inArchive && !archived ? <Loading /> : rows.length === 0 ? (
            query.q || query.project ? (
              <Empty title="Aucun ticket ne correspond." text="Essayez d'autres mots, ou un autre projet.">
                <span><Button variant="ghost" onClick={() => onQuery({ q: '', project: '' })}>Effacer la recherche</Button></span>
              </Empty>
            ) : inArchive ? (
              <Empty title="Aucun ticket archivé." text="Archivez un ticket livré, échoué ou arrêté pour alléger la liste : rien n'est effacé." />
            ) : (
              <Empty title="Aucune demande dans cette vue." text="Les tickets apparaissent ici quand l'équipe crée un ticket depuis l'onglet « À trier »." />
            )
          ) : (
            <div data-testid="tower-ticket-list">
              {groups.map((g) => {
                // « Tout archiver » : sur le groupe Terminés de « Tous », ou sur la vue filtrée.
                const bulkHere = canBulk && (query.filter !== 'tous' || g.key === 'termines')
                return (
                <section key={g.key} class="tw-group">
                  {(g.label || bulkHere) && (
                    <div class="tw-group-head">
                      {g.label && <h3 class="tw-overline">{g.label} <span class="tw-group-count">{g.rows.length}</span></h3>}
                      {bulkHere && (
                        <span class="tw-push">
                          <Button variant="ghost" disabled={busy} onClick={() => void archiveMany(bulk)} testId="tower-archive-all">
                            <Icons.Archive size={13} />Tout archiver ({bulk.length})
                          </Button>
                        </span>
                      )}
                    </div>
                  )}
                  <ul>
                    {g.rows.map((t) => (
                      <TicketLine key={t.id} t={t} selected={selected === t.code} busy={busy}
                        onSelect={() => onSelect(t.code)} onArchive={(on) => archiveOne(t, on)} />
                    ))}
                  </ul>
                </section>
                )
              })}
            </div>
          )}
        </div>
        <div class="tw-pane">
          {selected && <button type="button" class="tw-back fb-btn fb-btn-ghost" onClick={() => onSelect(null)}><Icons.ArrowLeft size={14} />Toutes les demandes</button>}
          {shown
            ? <TicketDetail ticket={shown} isApprover={isApprover} onChanged={onChanged} onClose={() => onSelect(null)}
                onArchived={() => !inArchive && onSelect(nextAfterRemoval(ordered, shown.code))} />
            : selected ? <Loading /> : (
              <Empty title="Choisissez une demande." text="Le détail montre les étapes, les contrôles, le contenu d'origine et la chronologie.">
                <p class="fb-muted tw-small tw-keys"><kbd>↑</kbd><kbd>↓</kbd> naviguer · <kbd>E</kbd> archiver · <kbd>/</kbd> rechercher · <kbd>Échap</kbd> fermer</p>
              </Empty>
            )}
        </div>
      </div>
    </div>
  )
}

// ── À trier ─────────────────────────────────────────────────────────────

function TriageCardView({ card, projects, onChanged }: { card: TowerTriageCard; projects: TowerProject[]; onChanged: () => void }): JSX.Element {
  const { confirm } = useFeedbaacks()
  const { busy, act } = useTowerAct(onChanged)
  const [editing, setEditing] = useState(false)
  const p = card.proposal
  const initial = () => ({
    project: p?.project ?? '', type: (p?.type ?? 'fix') as TowerTicketType, summary: p?.summary ?? '',
    criteria: (p?.criteria ?? []).join('\n'), severity: (p?.severity ?? 'medium') as TowerSeverity,
    also: p?.also ?? [],
  })
  const [draft, setDraft] = useState(initial)
  const blankPart = (): TowerProposalPart => ({ project: '', order: 'before', summary: '', criteria: [], reason: '' })
  const [part, setPart] = useState<TowerProposalPart | null>(null)
  const [partCriteria, setPartCriteria] = useState('')
  const addable = proposalAddable(projects, draft.project, draft.also)
  const addPart = () => {
    if (!part) return
    const crit = partCriteria.split('\n').map((c) => c.trim()).filter(Boolean)
    setDraft({ ...draft, also: [...draft.also, { ...part, summary: part.summary.trim(), reason: part.reason.trim(), criteria: crit }] })
    setPart(null)
    setPartCriteria('')
  }
  const chip = triageChip(card)
  const can = triageActions(card)
  const conf = p?.confidence ?? 0
  const save = () => act((c) => c.tower.editTriage(card.feedback_id, {
    project: draft.project, type: draft.type, summary: draft.summary, severity: draft.severity,
    criteria: draft.criteria.split('\n').map((s) => s.trim()).filter(Boolean), also: draft.also,
  }).then(() => setEditing(false)), 'Proposition corrigée.', 'Correction refusée')
  const also = editing ? draft.also : p?.also ?? []
  const refuse = async (report: boolean) => {
    const yes = await confirm(report
      ? { title: 'Refuser et signaler ?', message: 'Le retour est refusé et signalé comme tentative d\'injection.', confirmLabel: 'Refuser et signaler', destructive: true }
      : { title: 'Refuser ce retour ?', message: 'Aucun ticket ne sera créé. Le retour reste dans le tableau de suivi.', confirmLabel: 'Refuser', destructive: true })
    if (yes) void act((c) => c.tower.refuseTriage(card.feedback_id, report), report ? 'Retour refusé et signalé.' : 'Retour refusé.', 'Refus impossible')
  }

  return (
    <article class={cx('tw-card', card.verdict === 'injection' && 'is-danger')} data-testid={`tower-triage-${card.feedback_id}`}>
      <div class="tw-row">
        <Chip tone={chip.tone} testId="tower-triage-chip">{chip.label}</Chip>
        <span class="fb-muted tw-small">{sourceLabel(card.source, card.company)}</span>
        <span class="fb-muted tw-small tw-push">{relative(card.created_at)}</span>
      </div>
      {card.reasons.length > 0 && <div class={cx('tw-banner', card.verdict === 'injection' ? 'is-danger' : 'is-warning')}>{card.reasons.join(' · ')}</div>}
      <div class="tw-two">
        <UntrustedOriginal original={card.original} title="Retour d'origine" />
        <div class="tw-section">
          <h4 class="tw-overline">Ticket proposé</h4>
          {card.status === 'pending' && <p class="fb-muted tw-row"><Spinner size={14} />Le tri est en cours : la proposition arrive dans quelques secondes.</p>}
          {card.status === 'error' && <p class="tw-ko">Le tri a échoué : {card.error}. Il sera retenté automatiquement.</p>}
          {card.verdict === 'injection' && <p class="fb-muted">Aucun ticket ne peut naître d'un retour suspect : refusez-le et signalez-le.</p>}
          {p && !editing && card.verdict !== 'injection' && (
            <>
              <div class="tw-row tw-small">
                <span class="fb-mono">{p.project || 'projet inconnu'}</span>
                <Chip>{TYPE_LABEL[p.type]}</Chip>
                <Chip tone={SEVERITY_META[p.severity].tone}>{SEVERITY_META[p.severity].label}</Chip>
                <span class="tw-push tw-row fb-muted" title="Confiance du tri">
                  <span class="tw-bar is-short"><span class={`tw-tone-bg-${confidenceTone(conf)}`} style={{ width: `${Math.round(conf * 100)}%` }} /></span>
                  {Math.round(conf * 100)} %
                </span>
              </div>
              <p class="tw-summary">{p.summary}</p>
              {p.criteria.length > 0 && <ul class="tw-bullets">{p.criteria.map((c) => <li key={c}>{c}</li>)}</ul>}
            </>
          )}
          {also.length > 0 && card.verdict !== 'injection' && (
            <div class="tw-section" data-testid="tower-triage-also">
              <h4 class="tw-overline">Aussi dans cette demande</h4>
              <ul class="tw-demand">
                {also.map((a) => (
                  <li key={a.project}>
                    <span class="fb-mono">{a.project}</span>
                    <Chip>{a.order === 'before' ? `avant ${editing ? draft.project : p?.project ?? ''}` : `après ${editing ? draft.project : p?.project ?? ''}`}</Chip>
                    <span class="tw-small">{a.summary}</span>
                    <span class="fb-muted tw-small">{a.reason}</span>
                    {editing && (
                      <IconButton label={`Retirer ${a.project} de la demande`} onClick={() => setDraft({ ...draft, also: draft.also.filter((x) => x.project !== a.project) })}>
                        <Icons.X size={13} />
                      </IconButton>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {editing && (
            <div class="tw-section">
              <label class="dash-field">Projet
                <select class="fb-select" value={draft.project} onChange={(e) => {
                  // Le projet principal ne peut pas être aussi un projet supplémentaire.
                  const main = e.currentTarget.value
                  setDraft({ ...draft, project: main, also: draft.also.filter((a) => a.project !== main) })
                }}>
                  {projects.map((x) => <option key={x.key} value={x.key}>{x.label} ({x.key})</option>)}
                </select>
              </label>
              <div class="tw-row">
                <label class="dash-field">Type
                  <select class="fb-select" value={draft.type} onChange={(e) => setDraft({ ...draft, type: e.currentTarget.value as TowerTicketType })}>
                    {(['fix', 'feature', 'chore'] as const).map((v) => <option key={v} value={v}>{TYPE_LABEL[v]}</option>)}
                  </select>
                </label>
                <label class="dash-field">Gravité
                  <select class="fb-select" value={draft.severity} onChange={(e) => setDraft({ ...draft, severity: e.currentTarget.value as TowerSeverity })}>
                    {(['low', 'medium', 'high', 'critical'] as const).map((v) => <option key={v} value={v}>{SEVERITY_META[v].label}</option>)}
                  </select>
                </label>
              </div>
              <label class="dash-field">Résumé
                <textarea class="fb-input" rows={3} value={draft.summary} onInput={(e) => setDraft({ ...draft, summary: e.currentTarget.value })} />
              </label>
              <label class="dash-field">Critères d'acceptation, un par ligne
                <textarea class="fb-input" rows={4} value={draft.criteria} onInput={(e) => setDraft({ ...draft, criteria: e.currentTarget.value })} />
              </label>
              {!part && addable.length > 0 && (
                <Button variant="ghost" onClick={() => setPart({ ...blankPart(), project: addable[0].key })} testId="tower-triage-also-add">
                  <Icons.Plus size={13} />Ajouter un projet à la demande
                </Button>
              )}
              {part && (
                <div class="tw-section tw-also-form" data-testid="tower-triage-also-form">
                  <div class="tw-row">
                    <label class="dash-field">Projet
                      <select class="fb-select" value={part.project} onChange={(e) => setPart({ ...part, project: e.currentTarget.value })} data-testid="tower-triage-also-project">
                        {addable.map((x) => <option key={x.key} value={x.key}>{x.label} ({x.key})</option>)}
                      </select>
                    </label>
                    <label class="dash-field">Ordre
                      <select class="fb-select" value={part.order} onChange={(e) => setPart({ ...part, order: e.currentTarget.value as TowerProposalPart['order'] })}>
                        <option value="before">Avant {draft.project} (il l'attendra)</option>
                        <option value="after">Après {draft.project}</option>
                      </select>
                    </label>
                  </div>
                  <label class="dash-field">Ce qu'il faut changer dans ce projet
                    <textarea class="fb-input" rows={2} value={part.summary} onInput={(e) => setPart({ ...part, summary: e.currentTarget.value })} data-testid="tower-triage-also-summary" />
                  </label>
                  <label class="dash-field">Critères d'acceptation, un par ligne
                    <textarea class="fb-input" rows={2} value={partCriteria} onInput={(e) => setPartCriteria(e.currentTarget.value)} data-testid="tower-triage-also-criteria" />
                  </label>
                  <label class="dash-field">Pourquoi la demande en a besoin
                    <input class="fb-input" value={part.reason} onInput={(e) => setPart({ ...part, reason: e.currentTarget.value })} data-testid="tower-triage-also-reason" />
                  </label>
                  <div class="tw-row">
                    <Button variant="ghost" onClick={() => setPart(null)}>Annuler</Button>
                    <span class="tw-push" />
                    <Button disabled={!part.summary.trim() || !part.reason.trim() || !partCriteria.trim()} onClick={addPart} testId="tower-triage-also-confirm">
                      <Icons.Plus size={13} />Ajouter
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      <div class="tw-actions">
        {can.report && <Button variant="danger" disabled={busy} testId="tower-triage-report" onClick={() => void refuse(true)}>Refuser et signaler</Button>}
        {can.reject && !editing && <Button variant="ghost" disabled={busy} testId="tower-triage-reject" onClick={() => void refuse(false)}>Refuser</Button>}
        {can.edit && !editing && <Button disabled={busy} onClick={() => setEditing(true)}><Icons.Pencil size={13} />Modifier</Button>}
        {editing && <Button variant="ghost" disabled={busy} onClick={() => {
          setDraft(initial())
          setPart(null)
          setEditing(false)
        }}>Annuler</Button>}
        {editing && <Button variant="primary" disabled={busy} onClick={save}>Enregistrer</Button>}
        {can.ask && !editing && (
          <Button disabled={busy} onClick={() => act((c) => c.tower.askApprover(card.feedback_id), 'Question envoyée à l\'approbateur.', 'Envoi impossible')}>
            Demander à l'approbateur
          </Button>
        )}
        {can.create && !editing && (
          <Button variant="primary" disabled={busy} testId="tower-triage-create"
            onClick={() => act((c) => c.tower.createTicket(card.feedback_id), (p?.also?.length ?? 0) > 0
              ? `Demande créée : ${1 + (p?.also?.length ?? 0)} tickets, chacun part quand ses amonts sont livrés.`
              : 'Ticket créé : il part dans la file des agents.', 'Création refusée')}>
            <Icons.Check size={14} />{(p?.also?.length ?? 0) > 0 ? `Créer la demande (${1 + (p?.also?.length ?? 0)} tickets)` : 'Créer le ticket'}
          </Button>
        )}
      </div>
    </article>
  )
}

export function TriageView({ cards, projects, onChanged }: { cards: TowerTriageCard[] | null; projects: TowerProject[]; onChanged: () => void }): JSX.Element {
  if (!cards) return <Loading />
  if (cards.length === 0) return <Empty title="Rien à trier." text="Un retour arrive ici quand l'équipe le transmet à la tour depuis son détail." />
  const asked = cards.filter((c) => c.status === 'asked')
  const open = cards.filter((c) => c.status !== 'asked')
  return (
    <div class="tw-stack is-narrow">
      {open.map((c) => <TriageCardView key={c.feedback_id} card={c} projects={projects} onChanged={onChanged} />)}
      {asked.length > 0 && (
        <>
          <h3 class="tw-overline tw-stack-title">En attente de l'approbateur · {asked.length}</h3>
          {asked.map((c) => <TriageCardView key={c.feedback_id} card={c} projects={projects} onChanged={onChanged} />)}
        </>
      )}
    </div>
  )
}

// ── Agents ──────────────────────────────────────────────────────────────

function Runners({ superAdmin }: { superAdmin: boolean }): JSX.Element | null {
  const { client, notify, confirm } = useFeedbaacks()
  const [runners, setRunners] = useState<TowerRunner[] | null>(null)
  const [name, setName] = useState('')
  const [fresh, setFresh] = useState<string | null>(null)
  const load = () => client.tower.runners.list().then(setRunners, () => setRunners([]))
  useEffect(() => {
    if (superAdmin) void load()
  }, [superAdmin])
  if (!superAdmin || !runners) return null
  const fail = (err: unknown) => notify('error', errorMessage(err, 'Action impossible'))
  return (
    <section class="tw-card tw-wide" data-testid="tower-runners">
      <h3 class="tw-strong">Jetons des dispatchers</h3>
      {fresh && (
        <div class="dash-fresh">
          <p>Jeton à copier maintenant : il ne sera plus jamais affiché.</p>
          <div class="dash-secret">
            <code class="fb-mono" data-testid="tower-runner-token">{fresh}</code>
            <IconButton label="Copier le jeton" onClick={() => navigator.clipboard?.writeText(fresh).then(() => notify('success', 'Jeton copié.'), () => {})}><Icons.Copy size={14} /></IconButton>
          </div>
        </div>
      )}
      <ul class="dash-list">
        {runners.map((r) => (
          <li key={r.id} class={cx(r.revoked_at && 'is-revoked')}>
            <span class="tw-strong">{r.name}</span>
            <span class="fb-muted tw-small">{r.last_seen_at ? `vu ${relative(r.last_seen_at)}` : 'jamais vu'}</span>
            {r.revoked_at ? <span class="fb-muted tw-small tw-push">révoqué</span> : (
              <span class="tw-push"><Button variant="ghost" onClick={async () => {
                if (!(await confirm({ title: 'Révoquer ce jeton ?', message: 'Le dispatcher qui l\'utilise ne pourra plus tirer de travail.', confirmLabel: 'Révoquer', destructive: true }))) return
                client.tower.runners.revoke(r.id).then(load, fail)
              }}>Révoquer</Button></span>
            )}
          </li>
        ))}
      </ul>
      <form class="dash-inline-form" onSubmit={(e) => {
        e.preventDefault()
        client.tower.runners.create(name.trim()).then((r) => {
          setFresh(r.token)
          setName('')
          return load()
        }, fail)
      }}>
        <input class="fb-input" placeholder="Nom du dispatcher" value={name} onInput={(e) => setName(e.currentTarget.value)} required />
        <Button type="submit" variant="primary">Créer un jeton</Button>
      </form>
    </section>
  )
}

/** Plafond journalier, modifié sur place. */
function CapEditor({ value, busy, onSave }: { value: number; busy: boolean; onSave: (v: number) => void }): JSX.Element {
  const [draft, setDraft] = useState<string | null>(null)
  if (draft === null) {
    return <Button variant="ghost" disabled={busy} onClick={() => setDraft(String(value))} testId="tower-cap-button"><Icons.Pencil size={13} />Modifier le plafond</Button>
  }
  const v = Number(draft.replace(',', '.'))
  const valid = draft.trim() !== '' && Number.isFinite(v) && v >= 0
  return (
    <form class="tw-row" onSubmit={(e) => {
      e.preventDefault()
      if (!valid) return
      onSave(v)
      setDraft(null)
    }}>
      <input class="fb-input tw-cap-input" inputMode="decimal" aria-label="Plafond journalier en dollars" value={draft} autoFocus
        data-testid="tower-cap-input" onInput={(e) => setDraft(e.currentTarget.value)} onKeyDown={(e) => e.key === 'Escape' && setDraft(null)} />
      <span class="fb-muted">$</span>
      <Button variant="ghost" onClick={() => setDraft(null)}>Annuler</Button>
      <Button type="submit" variant="primary" disabled={busy || !valid} testId="tower-cap-save">Enregistrer</Button>
    </form>
  )
}

export function AgentsView({ agents, superAdmin, onChanged }: { agents: TowerAgents | null; superAdmin: boolean; onChanged: () => void }): JSX.Element {
  const { busy, act, stop } = useTowerAct(onChanged)
  if (!agents) return <Loading />
  const ratio = agents.daily_cap_usd > 0 ? Math.min(1, agents.spent_today_usd / agents.daily_cap_usd) : 0
  const ticketHref = (code: string) => `${href({ name: 'tower' })}?ticket=${encodeURIComponent(code)}`
  return (
    <div class="tw-agents">
      <section class="tw-card">
        <h3 class="tw-strong">Dispatcher{agents.dispatchers.length > 1 ? 's' : ''}</h3>
        {agents.dispatchers.length === 0 && <p class="fb-muted">Aucun dispatcher ne s'est encore annoncé : rien ne peut être lancé.</p>}
        {agents.dispatchers.map((d) => (
          <dl key={d.runner_id + d.node} class="tw-dl">
            <dt>Connexion</dt>
            <dd><span class={cx('tw-dot', d.connected ? (agents.global_paused ? 'is-amber' : 'is-green') : 'is-red')} />
              {d.connected ? (agents.global_paused ? 'Connecté · lancements suspendus' : 'Connecté · tire le travail') : 'Déconnecté'}</dd>
            <dt>Cluster</dt><dd class="fb-mono">{d.cluster}</dd>
            <dt>Nœud</dt><dd class="fb-mono">{d.node}</dd>
            <dt>Dernier contact</dt><dd>{relative(d.last_seen_at)}</dd>
            <dt>Image</dt><dd class="fb-mono tw-truncate" title={d.image_digest}>{d.image_digest || d.version}</dd>
          </dl>
        ))}
      </section>
      <section class="tw-card">
        <h3 class="tw-strong">Budget du jour</h3>
        <div class="tw-row"><strong class="tw-big">{money(agents.spent_today_usd)}</strong><span class="fb-muted">sur {money(agents.daily_cap_usd)}</span></div>
        <span class="tw-bar"><span class={ratio > 0.8 ? 'tw-tone-bg-amber' : ''} style={{ width: `${ratio * 100}%` }} /></span>
        {Object.keys(agents.spent_by_project).length > 0 && (
          <ul class="tw-kv">{Object.entries(agents.spent_by_project).map(([k, v]) => <li key={k}><span class="fb-mono">{k}</span>{money(v)}</li>)}</ul>
        )}
        <p class="fb-muted tw-small">Tris et Jobs compris, depuis minuit (Paris). Plafond atteint : plus aucun lancement avant demain.</p>
        <div><CapEditor value={agents.daily_cap_usd} busy={busy}
          onSave={(v) => void act((c) => c.tower.controls({ daily_cap_usd: v }), 'Plafond enregistré.', 'Plafond refusé')} /></div>
      </section>
      <section class="tw-card tw-wide">
        <h3 class="tw-strong">Capacité par projet</h3>
        <div class="tw-table-wrap">
          <table class="tw-table">
            <thead><tr><th>Projet</th><th>Agents actifs</th><th>En file</th><th>Plafond</th><th>Lancements</th></tr></thead>
            <tbody>
              {agents.capacity.map((c) => (
                <tr key={c.project}>
                  <td class="fb-mono">{c.project}{!c.enabled && <span class="fb-muted tw-small"> · désactivé au registre</span>}</td>
                  <td>{c.active}</td><td>{c.queued}</td><td>{c.max}</td>
                  <td>
                    <span class="tw-row">
                      {c.paused && <Chip tone="amber">en pause</Chip>}
                      <Button variant="ghost" disabled={busy} testId={`tower-pause-${c.project}`}
                        onClick={() => act((cl) => cl.tower.controls({ project: c.project, project_paused: !c.paused }), c.paused ? 'Projet repris.' : 'Projet en pause.', 'Action impossible')}>
                        {c.paused ? 'Reprendre' : 'Mettre en pause'}
                      </Button>
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
      <section class="tw-card tw-wide">
        <h3 class="tw-strong">Jobs en cours</h3>
        {agents.running.length === 0 ? <p class="fb-muted">Aucun Job ne tourne en ce moment.</p> : (
          <div class="tw-table-wrap">
            <table class="tw-table">
              <thead><tr><th>Ticket</th><th>Job</th><th>Modèle</th><th>Durée</th><th>Budget consommé</th><th>Dernière ligne</th><th /></tr></thead>
              <tbody>
                {agents.running.map((j) => (
                  <tr key={j.id}>
                    <td class="fb-mono">{j.ticket && <a href={ticketHref(j.ticket)} onClick={(e) => onLinkClick(e, ticketHref(j.ticket ?? ''))}>{j.ticket}</a>}</td>
                    <td class="fb-mono">{j.stage}</td>
                    <td class="fb-mono fb-muted">{j.model || 'aucun'}</td>
                    <td>{j.started_at ? durationLabel((Date.now() - new Date(j.started_at).getTime()) / 1000) : 'en attente'}</td>
                    <td>{money(j.cost_usd)} / {money(j.budget_usd ?? 0)}</td>
                    <td class="tw-truncate fb-muted" title={j.last_line}>{j.last_line}</td>
                    <td>{j.ticket && <Button variant="ghost" disabled={busy} onClick={() => void stop({ id: j.ticket ?? '', code: j.ticket ?? '' })}>Arrêter</Button>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
      <Runners superAdmin={superAdmin} />
    </div>
  )
}

// ── Projets ─────────────────────────────────────────────────────────────

export function ProjectsView({ projects }: { projects: TowerProject[] }): JSX.Element {
  return (
    <div class="tw-stack">
      <div class="tw-banner is-info">
        <span>Registre en lecture seule. Il se modifie par PR (<code class="fb-mono">server/internal/tower/projects.yaml</code>, ou le fichier FEEDBAACKS_TOWER_REGISTRY) et se valide au démarrage.</span>
      </div>
      <div class="tw-projects" data-testid="tower-projects">
        {projects.map((p) => (
          <article key={p.key} class={cx('tw-card', !p.enabled && 'is-off')}>
            <div class="tw-row">
              <h3 class="tw-strong">{p.label}</h3>
              <code class="fb-mono fb-muted tw-small">{p.key}</code>
              <span class="tw-push"><Chip tone={p.enabled ? 'emerald' : 'muted'}>{p.enabled ? 'Actif' : 'Désactivé'}</Chip></span>
            </div>
            <dl class="tw-dl">
              <dt>Repo</dt><dd class="fb-mono">{p.repo} · {p.default_branch}</dd>
              <dt>Apps servies</dt><dd class="fb-mono">{p.apps?.join(', ') || <span class="fb-muted">au choix de l'équipe</span>}</dd>
              <dt>Exécution</dt><dd>{p.runtime} · {p.max_parallel} en parallèle</dd>
              <dt>Budget</dt><dd>{money(p.budget.max_usd)} · {p.budget.max_minutes} min par ticket</dd>
              <dt>Autonomie</dt><dd>jusqu'à {p.max_level}</dd>
              <dt>Preview</dt><dd class="fb-mono">{p.preview.kind === 'none' ? <span class="fb-muted">aucune</span> : `t-<n>-${p.preview.host_suffix}`}</dd>
              <dt>Approbateurs</dt><dd>{p.approver_names.join(', ') || <span class="fb-muted">à désigner</span>}</dd>
            </dl>
          </article>
        ))}
      </div>
    </div>
  )
}
